import { useEffect, useState } from "react";
import { FileWarning, Loader2 } from "lucide-react";
import { fetchAuthedBlob, resolveAuthedUrl } from "@/api/client";
import { cn } from "@/lib/utils";

/**
 * Rendering bytes that need a bearer token.
 *
 * Every file the API serves is behind `AuthGuard`, which reads the token from
 * the `Authorization` header — and `<img src>` and `<iframe src>` send no
 * headers at all. Pointing either straight at a byte route gets a 401 that the
 * browser then blocks (`ERR_BLOCKED_BY_ORB`), which shows up as a broken image
 * and nothing in the console explaining it.
 *
 * So the bytes are fetched properly, with the header, and handed to the element
 * as a blob URL. That also keeps every credential out of the DOM: the
 * alternative — putting the presigned URL the route redirects to in `src` —
 * pastes a working, if short-lived, capability into the page for anything with
 * a DOM inspector or a screenshot to pick up. `AuthedFrame` is the one place
 * that pays that price, because Chrome leaves it no choice; the reasoning is
 * written out there and applies to nothing else in this file.
 *
 * Blob URLs are revoked when the component unmounts or the source changes.
 * Without that, scrolling a photo grid leaks every image it has ever rendered
 * for the lifetime of the tab.
 */
function useAuthedObjectUrl(
  path: string,
  type?: string,
): {
  url: string | null;
  failed: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);

    fetchAuthedBlob(path, type)
      .then((blob) => {
        // The component may have unmounted while the bytes were in flight, in
        // which case the URL is created and immediately orphaned — so it is
        // revoked here rather than left for a cleanup that has already run.
        objectUrl = URL.createObjectURL(blob);
        if (revoked) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!revoked) setFailed(true);
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, type]);

  return { url, failed };
}

/**
 * Save a file behind the guard to disk.
 *
 * A plain `<a href>` to a byte route does not work and never did: a top-level
 * navigation sends no `Authorization` header either, so the download route
 * answers 401 and the browser navigates to the error instead of saving
 * anything. The bytes are fetched with the header and handed to a synthesised
 * anchor.
 *
 * The object URL is revoked on the next tick rather than immediately — Safari
 * aborts the save if it is revoked in the same task as the click.
 */
export async function downloadAuthedFile(path: string, filename: string): Promise<void> {
  const blob = await fetchAuthedBlob(path);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** An `<img>` for a file behind the guard. */
export function AuthedImage({
  path,
  alt,
  className,
  fallbackClassName,
}: {
  /** An API path or absolute URL — a byte route that 302s to storage. */
  path: string;
  alt: string;
  className?: string;
  /** Applied to the placeholder and error states, which have no image to size. */
  fallbackClassName?: string;
}) {
  const { url, failed } = useAuthedObjectUrl(path);

  if (failed) {
    return (
      <span
        className={cn(
          "flex items-center justify-center bg-subtle text-muted",
          className,
          fallbackClassName,
        )}
        title="This image couldn’t be loaded"
      >
        <FileWarning size={18} aria-hidden />
      </span>
    );
  }

  if (!url) {
    return (
      <span
        className={cn(
          "flex animate-pulse items-center justify-center bg-subtle",
          className,
          fallbackClassName,
        )}
        aria-hidden
      />
    );
  }

  return <img src={url} alt={alt} className={className} />;
}

/**
 * The storage URL behind one of the 302 routes, for an element that must fetch
 * the bytes itself.
 *
 * The sibling of `useAuthedObjectUrl`, and the difference between them is the
 * origin the frame ends up on — see the note on the `<iframe>` in `AuthedFrame`
 * for why that is the entire point. Nothing is revoked on unmount because
 * nothing was allocated: the URL is a signed capability with a few minutes of
 * life, not a handle on bytes this tab is holding.
 */
function useAuthedStorageUrl(path: string): {
  url: string | null;
  failed: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);

    resolveAuthedUrl(path)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return { url, failed };
}

/**
 * What this component is willing to point a frame at.
 *
 * Not the security boundary — the frame's origin is, and the API decides the
 * `Content-Type` it signs into the URL. This is the honest-failure guard: a
 * `previewType` outside these two means the API would serve the object as an
 * `attachment` rather than inline, and the frame would sit there empty with
 * nothing on screen to say why. Better to say it.
 *
 * It is the server's `INLINE_VIEWABLE_TYPES` minus the images, which
 * `FilePreview` routes to `AuthedImage` instead. Restated rather than imported
 * because the two lists answer different questions — "what will the API serve
 * inline" and "what does this component know how to show" — and the day one of
 * them grows, the other should have to be thought about rather than follow.
 */
const FRAMEABLE_TYPES: readonly string[] = ["application/pdf", "text/plain"];

/**
 * An `<iframe>` for a file behind the guard — the document viewer's PDF pane.
 *
 * The browser's own PDF viewer is the whole point: real pages, zoom, text search
 * and print, with no PDF library in the bundle. Unlike everything else here, the
 * frame is pointed at storage rather than at a blob, which the `<iframe>` below
 * explains at length. The `#page=N` fragment works on a presigned URL exactly as
 * it does on a blob one — it is never sent to the server.
 */
export function AuthedFrame({
  path,
  title,
  fragment,
  className,
  type,
}: {
  path: string;
  title: string;
  /** e.g. `page=41`. Appended to the resolved URL, not to the fetched path. */
  fragment?: string;
  className?: string;
  /**
   * What the route says it will serve, from the document's `previewType`. Used
   * to decide whether to frame this at all and whether `#page=` means anything;
   * it does not type the bytes, because the API signs `ResponseContentType`
   * into the URL and the browser is going to believe that instead.
   */
  type: string;
}) {
  const frameable = FRAMEABLE_TYPES.includes(type.split(";")[0]?.trim().toLowerCase() ?? "");
  const { url, failed } = useAuthedStorageUrl(path);

  if (failed || !frameable) {
    return (
      <div className={cn("flex items-center justify-center bg-subtle", className)}>
        <p className="flex items-center gap-2 text-[0.88rem] font-semibold text-danger">
          <FileWarning size={16} aria-hidden />
          {frameable ? "This file couldn’t be loaded." : "This file can’t be shown here."}
        </p>
      </div>
    );
  }

  if (!url) {
    return (
      <div
        className={cn("flex items-center justify-center bg-subtle", className)}
        role="status"
        aria-live="polite"
      >
        <p className="flex items-center gap-2 text-[0.88rem] text-muted">
          <Loader2 size={16} className="animate-spin" aria-hidden />
          Opening the file…
        </p>
      </div>
    );
  }

  return (
    <iframe
      src={fragment ? `${url}#${fragment}` : url}
      title={title}
      className={className}
      // There is no `sandbox` here, and it is not an oversight. **Verified 5 Aug
      // 2026, Chrome 148:** the same PDF was framed six ways at once — no
      // sandbox, `allow-same-origin` (what this used to be), `allow-scripts`,
      // `allow-scripts allow-same-origin`, that plus `allow-popups`, and every
      // token short of top-navigation. Only the frame with no `sandbox`
      // attribute rendered. The other five showed Chrome's blocked-content
      // placeholder — the "This page has been blocked by Chrome" panel, drawn as
      // a torn-page icon at this size. The viewer is a privileged internal
      // document, not a scripted page we can grant our way into, so the choice
      // was never "which tokens" — it was what to put in a frame that cannot
      // have any.
      //
      // Which is why this is the one place that does *not* use a blob URL. A
      // blob inherits this document's origin, so an unsandboxed blob frame would
      // be same-origin with the console: one type confusion away from a document
      // that reads the session token out of `localStorage`. `resolveAuthedUrl`
      // follows the route's 302 and frames the presigned storage URL instead, so
      // the frame lands on the storage origin and the same-origin policy — not a
      // list in this file — is what keeps it away from our page.
      //
      // The cost, accepted deliberately: a working capability sits in the DOM
      // for anything with a devtools window open, which is the thing the note at
      // the top of this file avoids everywhere else. It is bounded — one object,
      // read-only, expiring in minutes, and already handed to this browser by
      // the 302 it followed to get here — and the alternative is a same-origin
      // frame with full privileges, which is worse in kind and not just degree.
      //
      // `referrerPolicy` is load-bearing now rather than tidy: without it every
      // preview would hand the storage host a `Referer` naming the project and
      // document being read.
      referrerPolicy="no-referrer"
    />
  );
}
